import React, { useState, useEffect, useCallback } from "react";

/**
 * LE REGISTRE DES CONSENTEMENTS AUX COOKIES, CÔTÉ ADMINISTRATION.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * COMPOSANT À PART, ET NON 200 LIGNES DE PLUS dans un tableau de bord qui en compte déjà
 * 18 000 : il est autonome — il va chercher ses données, gère son chargement et ses erreurs —
 * et peut donc être monté dans le SuperAdmin comme ailleurs sans rien dupliquer.
 *
 * ── CE QUE CET ÉCRAN SERT VRAIMENT À FAIRE ──
 * 1. PROUVER. Retrouver, pour une personne, ce qu'elle a choisi et quand. C'est la raison
 *    d'être du registre : sans lui, on répond de mémoire à une réclamation.
 * 2. CALIBRER GA4. C'est l'usage le moins évident et le plus rentable : si 35 % acceptent la
 *    mesure, Google Analytics ne voit qu'un tiers du trafic. Sans ce taux, on lit les
 *    chiffres GA4 comme s'ils couvraient tout le monde et on sous-estime son audience d'un
 *    facteur trois. Le taux est donc affiché EN GRAND, avec sa conséquence écrite à côté.
 * 3. DÉTECTER UN BANDEAU CASSÉ. Une chute du nombre de décisions ne veut pas dire que les
 *    visiteurs hésitent : elle veut dire que le bandeau ne s'affiche plus. C'est une panne
 *    silencieuse, qui peut durer des mois sans que rien ne casse visiblement.
 *
 * ── CE QU'IL NE FAUT PAS EN FAIRE, ET POURQUOI IL N'Y A PAS DE BOUTON POUR ──
 * Aucune action de ciblage n'est proposée ici. « Ceux qui ont refusé, traitons-les
 * autrement » transformerait un registre de protection en outil de ciblage — l'exact
 * contraire de sa raison d'être. L'écran lit, il n'exporte pas et ne segmente pas.
 *
 * ── LES COMPTEURS PORTENT SUR LA PAGE CHARGÉE, ET C'EST DIT À L'ÉCRAN ──
 * La route rend au plus 500 lignes. Présenter leur répartition comme un total donnerait un
 * taux d'acceptation faux dès que le registre dépasse ce plafond. La portée réelle est donc
 * affichée sous les chiffres, pas cachée dans une note de bas de page.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const ACTIONS = {
  accepte_tout: { label: "Tout accepté",  couleur: "#16a34a", fond: "#f0fdf4" },
  personnalise: { label: "Personnalisé",  couleur: "#0891b2", fond: "#e0f2fe" },
  refuse_tout:  { label: "Tout refusé",   couleur: "#b45309", fond: "#fef3c7" },
  retrait:      { label: "Retrait",       couleur: "#dc2626", fond: "#fef2f2" },
};

const PERIODES = [
  { cle: "7",   label: "7 jours" },
  { cle: "30",  label: "30 jours" },
  { cle: "90",  label: "90 jours" },
  { cle: "",    label: "Tout" },
];

export default function PanneauConfidentialite({ authHeaders }) {
  const [lignes, setLignes]   = useState([]);
  const [portee, setPortee]   = useState("");
  const [repart, setRepart]   = useState(null);
  const [charge, setCharge]   = useState(false);
  const [erreur, setErreur]   = useState("");
  const [periode, setPeriode] = useState("30");
  const [recherche, setRecherche] = useState("");

  const charger = useCallback(async () => {
    setCharge(true); setErreur("");
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (periode) {
        const d = new Date();
        d.setDate(d.getDate() - parseInt(periode, 10));
        params.set("depuis", d.toISOString());
      }
      if (recherche.trim()) params.set("email", recherche.trim());

      const r = await fetch(`${API_URL}/api/consentements?${params}`, { headers: authHeaders?.() || {} });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        /* LE 500 LE PLUS PROBABLE EST UNE TABLE ABSENTE : le script SQL n'a pas encore été
           joué. On le dit, plutôt que de laisser « erreur 500 » sans piste. */
        throw new Error(d.error?.includes("consentements_cookies")
          ? "La table n’existe pas encore — exécutez Backend/sql/consentements_cookies.sql."
          : (d.error || `Erreur ${r.status}`));
      }
      const d = await r.json();
      setLignes(d.consentements || []);
      setPortee(d.portee || "");
      setRepart(d.repartition || null);
    } catch (e) {
      setErreur(e.message);
      setLignes([]); setRepart(null);
    } finally { setCharge(false); }
  }, [periode, recherche, authHeaders]);

  useEffect(() => { charger(); }, [periode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* LE TAUX NE PORTE QUE SUR LES DÉCISIONS INITIALES. Un retrait n'est pas un refus de
     plus : le compter comme tel ferait baisser le taux deux fois pour une même personne —
     une fois quand elle accepte, une fois quand elle se rétracte. */
  const decisions = (repart?.accepte_tout || 0) + (repart?.personnalise || 0) + (repart?.refuse_tout || 0);
  const acceptantMesure = lignes.filter(l => l.action !== "retrait" && l.mesure).length;
  const taux = decisions ? Math.round((acceptantMesure / decisions) * 100) : null;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
          🔒 Politique de confidentialité — registre des consentements
        </h3>
        <p style={{ margin: 0, fontSize: 12.5, color: "#6b7280", lineHeight: 1.6 }}>
          Chaque décision prise dans le bandeau cookies du site public y est consignée.
          Une ligne par décision : un visiteur qui accepte puis se rétracte en produit deux.
        </p>
      </div>

      {/* ── Filtres ── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {PERIODES.map(p => (
            <button key={p.cle} onClick={() => setPeriode(p.cle)} style={{
              padding: "7px 14px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
              border: periode === p.cle ? "none" : "1.5px solid #e2e8f0",
              background: periode === p.cle ? "#0f172a" : "#fff",
              color: periode === p.cle ? "#fff" : "#475569",
            }}>{p.label}</button>
          ))}
        </div>
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          onKeyDown={e => e.key === "Enter" && charger()}
          placeholder="Rechercher un e-mail…"
          style={{ flex: "1 1 220px", minWidth: 180, padding: "8px 13px", borderRadius: 9,
            border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none", fontFamily: "inherit" }}
        />
        <button onClick={charger} style={{
          padding: "8px 16px", borderRadius: 9, border: "1.5px solid #e2e8f0",
          background: "#fff", color: "#334155", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
        }}>🔄 Actualiser</button>
      </div>

      {erreur && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
          padding: "12px 16px", fontSize: 13, color: "#b91c1c", marginBottom: 18, lineHeight: 1.6 }}>
          {erreur}
        </div>
      )}

      {charge && <div style={{ padding: "30px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>⏳ Chargement…</div>}

      {!charge && !erreur && (
        <>
          {/* ══ LE TAUX D'ACCEPTATION, ET CE QU'IL IMPLIQUE ══
              Un pourcentage seul ne dit rien à qui ne connaît pas le sujet. La phrase à
              côté transforme le chiffre en information exploitable : elle dit comment lire
              les rapports GA4 à partir de ce taux. */}
          {taux !== null && (
            <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap",
              background: "linear-gradient(135deg,#011451,#0c33ac)", borderRadius: 14,
              padding: "20px 24px", marginBottom: 18, color: "#fff" }}>
              <div style={{ textAlign: "center", minWidth: 110 }}>
                <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1 }}>{taux}%</div>
                <div style={{ fontSize: 11.5, opacity: .72, marginTop: 6 }}>acceptent la mesure</div>
              </div>
              <div style={{ flex: 1, minWidth: 240, fontSize: 13, lineHeight: 1.7, opacity: .9 }}>
                Google Analytics ne voit donc qu’environ <strong>{taux}%</strong> de votre trafic réel.
                {taux > 0 && taux < 100 && (
                  <> Pour estimer l’audience véritable, multipliez ses chiffres par
                  {" "}<strong>{(100 / taux).toFixed(1)}</strong>.</>
                )}
              </div>
            </div>
          )}

          {/* Répartition par geste */}
          {repart && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 8 }}>
              {Object.entries(ACTIONS).map(([cle, a]) => (
                <div key={cle} style={{ background: a.fond, border: `1px solid ${a.couleur}22`,
                  borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: a.couleur, lineHeight: 1 }}>
                    {repart[cle] ?? 0}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 5, fontWeight: 600 }}>{a.label}</div>
                </div>
              ))}
            </div>
          )}

          {portee && (
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 18 }}>
              Ces chiffres portent sur les {portee}, pas sur l’ensemble du registre.
            </div>
          )}

          {/* ── Le détail ── */}
          {lignes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13, lineHeight: 1.7 }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              Aucune décision enregistrée sur cette période.<br />
              <span style={{ fontSize: 12 }}>
                Si le site reçoit des visites, cela peut signaler que le bandeau ne s’affiche plus.
              </span>
            </div>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5, minWidth: 680 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Date", "Décision", "Mesure", "Marketing", "Personne", "Version", "Page"].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lignes.map(l => {
                    const a = ACTIONS[l.action] || { label: l.action, couleur: "#64748b", fond: "#f1f5f9" };
                    return (
                      <tr key={l.id}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {new Date(l.cree_le).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td style={td}>
                          <span style={{ background: a.fond, color: a.couleur, borderRadius: 999,
                            padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{a.label}</span>
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>{l.mesure ? "✓" : "—"}</td>
                        <td style={{ ...td, textAlign: "center" }}>{l.marketing ? "✓" : "—"}</td>
                        <td style={td}>
                          {l.email
                            ? <span style={{ fontWeight: 600, color: "#0f172a" }}>{l.email}</span>
                            : <span style={{ color: "#94a3b8", fontStyle: "italic" }}>visiteur anonyme</span>}
                        </td>
                        <td style={{ ...td, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11.5, whiteSpace: "nowrap" }}>
                          {l.version_texte}
                        </td>
                        <td style={{ ...td, color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {l.page || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16, background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: 10, padding: "12px 16px", fontSize: 12, color: "#64748b", lineHeight: 1.65 }}>
            <strong style={{ color: "#334155" }}>Ce registre ne sert pas au ciblage.</strong> Il
            existe pour prouver un consentement et honorer une demande d’accès. S’en servir pour
            traiter différemment ceux qui ont refusé en ferait l’inverse de ce qu’il protège.
          </div>
        </>
      )}
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 800, color: "#64748b",
  textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const td = { padding: "10px 14px", borderBottom: "1px solid #f1f5f9", color: "#475569", verticalAlign: "middle" };
